import React from "react";
import {
  Trans as BaseTrans,
  useTranslation as useBaseTranslation,
  initReactI18next,
} from "react-i18next";

import i18next from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";

import type { I18nProps } from "@kazoohr/types";

import { localesJsonMap } from "./localesJsonMap";
import { PluralFallbackPlugin } from "./pluralFallbackPlugin";
import { I18N_CONFIG } from "./sharedConfig";
import { useTranslationStub } from "./useTranslationStub";

/**
 * Configure the global i18next instance on the client.
 */
export async function initializeClientI18n(
  i18nProps: I18nProps
): Promise<typeof i18next> {
  const i18n = await initialize();
  await i18n.changeLanguage(i18nProps.language);
  return i18n;
}

/**
 * A minimal i18n instance that allows smaller apps to pass in a custom set of
 * locales to initialize with.
 */
export function minimalClientI18n({
  locales,
}: {
  locales: Record<string, Record<string, () => Promise<any>>>;
}) {
  return i18next
    .use(initReactI18next)
    .use(
      resourcesToBackend((language: string, namespace: string) => {
        return locales?.[language]?.[namespace]?.();
      })
    )
    .init({
      ...I18N_CONFIG,
    });
}

async function initialize() {
  i18next
    .use(initReactI18next)
    .use(PluralFallbackPlugin)
    .use(
      resourcesToBackend((language: string, namespace: string) => {
        // If it exists on the window object, use that
        const windowAsAny = window as any; // eslint-disable-line @typescript-eslint/consistent-type-assertions
        const windowResource =
          windowAsAny.kazoo?.i18nProps?.resources?.[language]?.[namespace];
        if (windowResource) {
          return windowResource;
        }

        return localesJsonMap?.[language]?.[namespace]?.();
      })
    )
    .init({
      ...I18N_CONFIG,
    });

  return i18next;
}

/**
 * In test mode, renders the translation string with any parameters.
 */
function isTestMode() {
  return typeof process === "object" && process?.env?.NODE_ENV === "test";
}

export function useTranslation(): {
  t: (key: string, arg2?: any, arg3?: any) => string;
} {
  if (isTestMode()) {
    // Ignore this because, yes, it violates the rules of hooks, but only if we're
    // switching between environments. We don't do that!
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useTranslationStub();
  }
  // Ignore this because, yes, it violates the rules of hooks, but only if we're
  // switching between environments. IRL, we don't do that!
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { t } = useBaseTranslation();

  const newT = (key: string, arg2?: any, arg3?: any) => {
    // Library code. We need to standardize how we use translations so it's
    // easier to upgrade our i18n libraries. We're casting here because we
    // don't support the wide range of weird types that react-i18n claims
    // to output.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return t(key, arg2, arg3) as string;
  };

  return { t: newT };
}

/**
 * A wrapper around react-i18next's Translation component to provide backward
 * compatibility with old type signatures. At runtime, they're compatible, but
 * there are weird readonly issues with react-i18n's types.
 *
 * Documentation for this method: https://react.i18next.com/latest/trans-component
 */
export function Trans(props: {
  i18nKey: string;
  components?: any[] | { [tagName: string]: React.ReactElement };
  count?: number;
  values?: Record<string, any>;
  ["data-testid"]?: string;
}) {
  if (isTestMode()) {
    return <span data-testid={props["data-testid"]}>{props.i18nKey}</span>;
  }
  return <BaseTrans {...props} />;
}
